import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Receipt, Loader2, Info } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  defaultPreferenzeFatturazioneRetta,
  labelModalitaRetta,
  MODALITA_RETTA,
  normalizePreferenzeFatturazioneRetta,
} from "../utils/preferenzeFatturazioneRetta";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Impostazioni predefinite retta in fattura per l'ospite (bozze automatiche + default manuale).
 */
export default function PreferenzeFatturazioneRetta({
  token,
  strutturaId,
  value,
  isEditing,
  onChange,
}) {
  const prefsReadonly = useMemo(
    () => normalizePreferenzeFatturazioneRetta(value),
    [value],
  );

  /** Stato locale in modifica: handleFieldChange del fascicolo non causa re-render del genitore. */
  const [editPrefs, setEditPrefs] = useState(() => normalizePreferenzeFatturazioneRetta(value));

  useEffect(() => {
    if (isEditing) {
      setEditPrefs(normalizePreferenzeFatturazioneRetta(value));
    }
  }, [isEditing, value]);

  const prefs = isEditing ? editPrefs : prefsReadonly;

  const [vociRettaConfig, setVociRettaConfig] = useState([]);
  const [loadingVoci, setLoadingVoci] = useState(false);

  const fetchVociConfig = useCallback(async () => {
    if (!token) return;
    setLoadingVoci(true);
    try {
      const params = strutturaId ? { strutturaId } : {};
      const res = await axios.get(`${API_URL}/api/fatturazione/config`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setVociRettaConfig(Array.isArray(res.data?.config?.voci_retta_mensile)
        ? res.data.config.voci_retta_mensile
        : []);
    } catch {
      setVociRettaConfig([]);
    } finally {
      setLoadingVoci(false);
    }
  }, [token, strutturaId]);

  useEffect(() => {
    fetchVociConfig();
  }, [fetchVociConfig]);

  const patch = (nextPatch) => {
    const next = normalizePreferenzeFatturazioneRetta({ ...editPrefs, ...nextPatch });
    setEditPrefs(next);
    onChange(next);
  };

  const handleModalita = (modalita) => {
    const next = { modalita };
    if (modalita === "esclusa") next.includi_retta = false;
    else next.includi_retta = true;
    patch(next);
  };

  const vociSelezionate =
    Array.isArray(prefs.voci_incluse) && prefs.voci_incluse.length > 0
      ? prefs.voci_incluse
      : vociRettaConfig.map(v => v.id).filter(Boolean);

  return (
    <Card className="card-base border-slate-200 md:col-span-2">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base text-slate-800">
          <Receipt className="w-4 h-4 text-emerald-600" />
          Fatturazione retta (predefinita)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-slate-500 flex items-start gap-2">
          <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          Usata dal generatore delle bozze automatiche (Controllo mensile) e come impostazione iniziale
          quando generi manualmente la fattura dal fascicolo. Puoi sempre modificarla al momento della generazione.
        </p>

        {isEditing ? (
          <>
            <div>
              <Label className="text-slate-500 text-xs">Modalità retta in fattura</Label>
              <Select value={prefs.modalita} onValueChange={handleModalita}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Seleziona modalità" />
                </SelectTrigger>
                <SelectContent>
                  {MODALITA_RETTA.map(m => (
                    <SelectItem key={m.value} value={m.value}>
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {prefs.modalita === "parziale" && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3 space-y-2">
                <Label className="text-slate-700 text-xs font-medium">
                  Importo fisso da fatturare ogni mese (€)
                </Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  className="max-w-xs bg-white"
                  value={prefs.importo_parziale ?? ""}
                  onChange={e => {
                    const v = e.target.value;
                    patch({
                      importo_parziale: v === "" ? null : parseFloat(v.replace(",", ".")) || null,
                    });
                  }}
                  placeholder="Es. 400"
                  autoFocus
                />
                <p className="text-[11px] text-slate-500">
                  Se il residuo del mese è inferiore, verrà fatturato solo il residuo.
                </p>
              </div>
            )}

            {prefs.modalita === "senza_importo" && (
              <p className="text-xs text-slate-500">
                La riga soggiorno comparirà con importo € 0,00 (descrizione senza addebito).
              </p>
            )}

            {loadingVoci ? (
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Caricamento voci retta struttura…
              </div>
            ) : vociRettaConfig.length > 0 && prefs.modalita !== "esclusa" && prefs.modalita !== "senza_importo" ? (
              <div className="space-y-2">
                <Label className="text-slate-500 text-xs">Voci retta da includere (config struttura)</Label>
                {vociRettaConfig.map(voce => {
                  const checked = vociSelezionate.includes(voce.id);
                  return (
                    <div key={voce.id} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        id={`pref-voce-retta-${voce.id}`}
                        checked={checked}
                        onChange={e => {
                          const ids = vociRettaConfig.map(v => v.id).filter(Boolean);
                          const base = Array.isArray(prefs.voci_incluse) ? [...prefs.voci_incluse] : [...ids];
                          const next = e.target.checked
                            ? [...new Set([...base, voce.id])]
                            : base.filter(id => id !== voce.id);
                          patch({ voci_incluse: next });
                        }}
                        className="rounded"
                      />
                      <Label htmlFor={`pref-voce-retta-${voce.id}`} className="font-normal cursor-pointer">
                        {voce.nome || "Voce"} ({Number(voce.percentuale || 0).toLocaleString("it-IT")}%)
                      </Label>
                    </div>
                  );
                })}
              </div>
            ) : null}
          </>
        ) : (
          <div className="text-sm space-y-1">
            <p className="font-medium text-slate-800">{labelModalitaRetta(prefs.modalita)}</p>
            {prefs.modalita === "parziale" && prefs.importo_parziale != null && (
              <p className="text-slate-600">
                Importo fisso: € {Number(prefs.importo_parziale).toLocaleString("it-IT", { minimumFractionDigits: 2 })}
              </p>
            )}
            {vociRettaConfig.length > 0 &&
              prefs.modalita !== "esclusa" &&
              prefs.modalita !== "senza_importo" &&
              Array.isArray(prefs.voci_incluse) &&
              prefs.voci_incluse.length > 0 &&
              prefs.voci_incluse.length < vociRettaConfig.length && (
                <p className="text-xs text-slate-500">
                  Voci incluse:{" "}
                  {vociRettaConfig
                    .filter(v => prefs.voci_incluse.includes(v.id))
                    .map(v => v.nome)
                    .join(", ")}
                </p>
              )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export { defaultPreferenzeFatturazioneRetta };
